import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Package,
  AlertTriangle,
  Trash2,
  RefreshCw,
  GitMerge,
  Copy as CopyIcon,
  CheckSquare,
  Square,
  X,
  Boxes,
  Unlock,
  ArrowRightLeft,
  ListChecks,
  TrendingUp,
  ClipboardCheck,
  ShieldAlert,
} from "lucide-react";
import SaleRequirementsView from "@/pages/stock/SaleRequirementsView";
import MyTasksView from "@/pages/stock/MyTasksView";
import ReconciliationView from "@/pages/stock/ReconciliationView";

// ---------- visual helpers ----------
const PRIORITY_STYLE = {
  urgent: { color: "bg-red-100 text-red-700 border-red-200", order: 0 },
  high: { color: "bg-orange-100 text-orange-700 border-orange-200", order: 1 },
  normal: { color: "bg-slate-100 text-slate-700 border-slate-200", order: 2 },
  low: { color: "bg-gray-100 text-gray-500 border-gray-200", order: 3 },
};

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-blue-100 text-blue-800",
  dispatched: "bg-indigo-100 text-indigo-800",
  partial: "bg-purple-100 text-purple-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-gray-100 text-gray-500",
};

const SOURCE_STYLE = {
  manual: "bg-[#9E6D5B]/10 text-[#9E6D5B]",
  low_stock: "bg-rose-100 text-rose-700",
  requirement: "bg-teal-100 text-teal-700",
};

const PRIORITIES = ["urgent", "high", "normal", "low"];

// ---------- main component ----------
export default function ManagementTab({ shops = [] }) {
  const [subTab, setSubTab] = useState("queue"); // 'queue' | 'shop_reqs'
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({});
  // filters
  const [shopFilter, setShopFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [search, setSearch] = useState("");
  // selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  // dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [duplicateState, setDuplicateState] = useState(null); // { existing, payload }
  const [reserveItem, setReserveItem] = useState(null); // restock item to reserve against

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (shopFilter !== "all") params.shop_id = shopFilter;
      if (statusFilter === "open") {
        // backend default
      } else if (statusFilter === "all") {
        params.include_closed = true;
      } else {
        params.status = statusFilter;
      }
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/management/restock-queue", { params });
      setItems(data.items || []);
      setKpis(data.kpis || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [shopFilter, statusFilter, sourceFilter, priorityFilter, search]);

  useEffect(() => {
    const t = setTimeout(fetchQueue, 250);
    return () => clearTimeout(t);
  }, [fetchQueue]);

  // Refetch the queue whenever the user returns to the Restock Queue sub-tab,
  // so items added from other sub-tabs (e.g. Shop Requirements -> Add to Queue)
  // appear instantly without a manual refresh.
  useEffect(() => {
    if (subTab === "queue") fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  // ---------- mutations ----------
  const submitManualRequest = async (payload, strategy = "ask") => {
    try {
      const { data } = await api.post("/management/restock-queue", {
        ...payload,
        duplicate_strategy: strategy,
      });
      if (data.merged) toast.success("Merged into existing request");
      else toast.success("Restock request created");
      setAddOpen(false);
      setDuplicateState(null);
      fetchQueue();
    } catch (e) {
      // duplicate?
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.code === "duplicate_request") {
        setDuplicateState({ existing: detail.existing, payload });
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  const updateItem = async (id, patch) => {
    try {
      await api.put(`/management/restock-queue/${id}`, patch);
      toast.success("Updated");
      fetchQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this restock request?")) return;
    try {
      await api.delete(`/management/restock-queue/${id}`);
      toast.success("Deleted");
      setSelectedIds((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      fetchQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected request(s)?`)) return;
    try {
      const { data } = await api.post("/management/restock-queue/bulk-delete", {
        ids: Array.from(selectedIds),
      });
      toast.success(`Deleted ${data.deleted}${data.skipped?.length ? ` · skipped ${data.skipped.length}` : ""}`);
      setSelectedIds(new Set());
      fetchQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Phase 2 — reservation mutations
  const reserveStock = async (itemId, sourceShopId, quantity) => {
    try {
      await api.post(`/management/restock-queue/${itemId}/reserve`, {
        source_shop_id: sourceShopId,
        quantity: Number(quantity),
      });
      toast.success(`Reserved ${quantity} units`);
      setReserveItem(null);
      fetchQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const releaseReservation = async (itemId, reservationId) => {
    if (!window.confirm("Release this reservation?")) return;
    try {
      await api.post(
        `/management/restock-queue/${itemId}/release-reservation/${reservationId}`
      );
      toast.success("Reservation released");
      fetchQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ---------- selection helpers ----------
  const allIds = useMemo(() => items.map((i) => i.id), [items]);
  const allChecked = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const toggleAll = () => {
    if (allChecked) setSelectedIds(new Set());
    else setSelectedIds(new Set(allIds));
  };
  const toggleOne = (id) =>
    setSelectedIds((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  // ---------- render ----------
  return (
    <div className="space-y-4" data-testid="management-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg">Management Control Center</h2>
          <p className="text-xs text-muted-foreground">
            Restocking, inter-shop transfers, reservations, and shop-driven requirements — all in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {subTab === "queue" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchQueue}
                data-testid="mgmt-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: "#9E6D5B" }}
                onClick={() => setAddOpen(true)}
                data-testid="mgmt-add-request-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Manual Request
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Sub-tab toggle */}
      <div className="flex gap-1 border-b" data-testid="mgmt-subtabs">
        <button
          onClick={() => setSubTab("queue")}
          className={`px-4 py-2 text-sm flex items-center gap-1.5 transition border-b-2 -mb-px ${
            subTab === "queue"
              ? "border-[#9E6D5B] text-[#9E6D5B] font-semibold"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="mgmt-subtab-queue"
        >
          <ListChecks className="w-4 h-4" />
          Restock Queue
        </button>
        <button
          onClick={() => setSubTab("my_tasks")}
          className={`px-4 py-2 text-sm flex items-center gap-1.5 transition border-b-2 -mb-px ${
            subTab === "my_tasks"
              ? "border-[#9E6D5B] text-[#9E6D5B] font-semibold"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="mgmt-subtab-my-tasks"
        >
          <ClipboardCheck className="w-4 h-4" />
          My Tasks
        </button>
        <button
          onClick={() => setSubTab("shop_reqs")}
          className={`px-4 py-2 text-sm flex items-center gap-1.5 transition border-b-2 -mb-px ${
            subTab === "shop_reqs"
              ? "border-[#9E6D5B] text-[#9E6D5B] font-semibold"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="mgmt-subtab-shop-reqs"
        >
          <TrendingUp className="w-4 h-4" />
          Shop Requirements (from Sales)
        </button>
        <button
          onClick={() => setSubTab("reconciliation")}
          className={`px-4 py-2 text-sm flex items-center gap-1.5 transition border-b-2 -mb-px ${
            subTab === "reconciliation"
              ? "border-[#9E6D5B] text-[#9E6D5B] font-semibold"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="mgmt-subtab-reconciliation"
        >
          <ShieldAlert className="w-4 h-4" />
          Reconciliation
        </button>
      </div>

      {subTab === "shop_reqs" ? (
        <SaleRequirementsView shops={shops} onQueueChange={fetchQueue} />
      ) : subTab === "my_tasks" ? (
        <MyTasksView shops={shops} onJumpToQueue={() => setSubTab("queue")} />
      ) : subTab === "reconciliation" ? (
        <ReconciliationView shops={shops} />
      ) : (
        <>
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="mgmt-kpis">
        <KpiCard label="Open Requests" value={kpis.total_open ?? 0} />
        <KpiCard label="Units Pending" value={kpis.total_qty_pending ?? 0} mono />
        <KpiCard label="Urgent" value={kpis.urgent_count ?? 0} tone="urgent" />
        <KpiCard label="High Priority" value={kpis.high_count ?? 0} tone="high" />
      </div>

      {/* Filters */}
      <Card className="p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
          {/* search */}
          <div className="relative sm:col-span-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-8 h-9"
              placeholder="Search by product, brand, barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="mgmt-search-input"
            />
          </div>
          <Select value={shopFilter} onValueChange={setShopFilter}>
            <SelectTrigger className="h-9" data-testid="mgmt-shop-filter">
              <SelectValue placeholder="All shops" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All shops</SelectItem>
              {shops.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9" data-testid="mgmt-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open (default)</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="dispatched">Dispatched</SelectItem>
              <SelectItem value="partial">Partially fulfilled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All (incl. closed)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="h-9" data-testid="mgmt-priority-filter">
              <SelectValue placeholder="All priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Source:</span>
          {["all", "manual", "low_stock", "requirement"].map((src) => (
            <button
              key={src}
              onClick={() => setSourceFilter(src)}
              className={`px-2 py-0.5 rounded-full border transition ${
                sourceFilter === src
                  ? "bg-[#9E6D5B] text-white border-[#9E6D5B]"
                  : "bg-white text-muted-foreground border-gray-200 hover:border-[#9E6D5B]"
              }`}
              data-testid={`mgmt-source-chip-${src}`}
            >
              {src === "all" ? "All" : src.replace("_", " ")}
            </button>
          ))}
          {selectedIds.size > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-muted-foreground">{selectedIds.size} selected</span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                onClick={bulkDelete}
                data-testid="mgmt-bulk-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete selected
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No restock requests match your filters.</p>
            <p className="text-xs mt-1">Click <strong>+ Add Manual Request</strong> to create one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs">
                <tr>
                  <th className="p-2 w-[36px] text-center">
                    <button onClick={toggleAll} aria-label="Toggle all">
                      {allChecked ? (
                        <CheckSquare className="w-4 h-4 text-[#9E6D5B]" />
                      ) : (
                        <Square className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                  </th>
                  <th className="p-2 text-left">Priority</th>
                  <th className="p-2 text-left">Product</th>
                  <th className="p-2 text-left">Destination Shop</th>
                  <th className="p-2 text-right">Qty (req / done)</th>
                  <th className="p-2 text-left">Source</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Reservations</th>
                  <th className="p-2 text-left">Requested By</th>
                  <th className="p-2 text-left">Created</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <RestockRow
                    key={it.id}
                    item={it}
                    checked={selectedIds.has(it.id)}
                    onToggle={() => toggleOne(it.id)}
                    onChangePriority={(p) => updateItem(it.id, { priority: p })}
                    onChangeStatus={(s) => updateItem(it.id, { status: s })}
                    onDelete={() => deleteItem(it.id)}
                    onReserve={() => setReserveItem(it)}
                    onReleaseReservation={(rid) => releaseReservation(it.id, rid)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add Manual Request Dialog */}
      <ManualRequestDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        shops={shops}
        onSubmit={(payload) => submitManualRequest(payload, "ask")}
      />

      {/* Duplicate Resolution Dialog */}
      <DuplicateDialog
        state={duplicateState}
        onCancel={() => setDuplicateState(null)}
        onMerge={() => submitManualRequest(duplicateState.payload, "merge")}
        onSeparate={() => submitManualRequest(duplicateState.payload, "separate")}
      />

      {/* Phase 2 — Reserve Stock Dialog */}
      <ReserveStockDialog
        item={reserveItem}
        shops={shops}
        onCancel={() => setReserveItem(null)}
        onConfirm={(sourceShopId, qty) =>
          reserveStock(reserveItem.id, sourceShopId, qty)
        }
      />
        </>
      )}
    </div>
  );
}

// ---------- subcomponents ----------
function KpiCard({ label, value, mono, tone }) {
  const accent =
    tone === "urgent" ? "text-red-600" : tone === "high" ? "text-orange-600" : "text-slate-800";
  return (
    <Card className="p-3" data-testid={`mgmt-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${accent} ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
    </Card>
  );
}

function RestockRow({
  item,
  checked,
  onToggle,
  onChangePriority,
  onChangeStatus,
  onDelete,
  onReserve,
  onReleaseReservation,
}) {
  const p = item.product_snapshot || {};
  const created = item.created_at ? formatDateTime(item.created_at) : "—";
  const activeReservations = (item.reservations || []).filter((r) => !r.released_at);
  const totalReserved = activeReservations.reduce(
    (sum, r) => sum + Number(r.quantity || 0),
    0
  );
  const remaining = Math.max(
    0,
    Number(item.quantity_requested) -
      Number(item.quantity_fulfilled || 0) -
      totalReserved
  );
  const canReserve =
    item.status !== "completed" && item.status !== "cancelled" && remaining > 0;
  return (
    <tr className="border-t hover:bg-muted/30" data-testid={`mgmt-row-${item.id}`}>
      <td className="p-2 text-center">
        <button onClick={onToggle} aria-label="Toggle row">
          {checked ? (
            <CheckSquare className="w-4 h-4 text-[#9E6D5B]" />
          ) : (
            <Square className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      </td>
      <td className="p-2">
        <Select value={item.priority} onValueChange={onChangePriority}>
          <SelectTrigger
            className={`h-7 w-[100px] text-xs border ${PRIORITY_STYLE[item.priority]?.color || ""}`}
            data-testid={`mgmt-row-priority-${item.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="p-2">
        <div className="font-medium text-sm">{p.product_name || "—"}</div>
        <div className="text-[11px] text-muted-foreground">
          {p.brand_name || "—"} · {p.auto_barcode || ""}
        </div>
      </td>
      <td className="p-2 text-sm">
        <Badge variant="outline">{item.destination_shop_name}</Badge>
      </td>
      <td className="p-2 text-right font-mono text-sm">
        <span className="font-semibold">{item.quantity_requested}</span>
        <span className="text-muted-foreground"> / {item.quantity_fulfilled}</span>
      </td>
      <td className="p-2">
        <span
          className={`inline-block px-2 py-0.5 rounded text-[11px] ${SOURCE_STYLE[item.source] || ""}`}
        >
          {item.source.replace("_", " ")}
        </span>
      </td>
      <td className="p-2">
        <Select value={item.status} onValueChange={onChangeStatus}>
          <SelectTrigger
            className={`h-7 w-[130px] text-xs border-0 ${STATUS_STYLE[item.status] || ""}`}
            data-testid={`mgmt-row-status-${item.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="p-2 text-xs" data-testid={`mgmt-row-reservations-${item.id}`}>
        {activeReservations.length === 0 ? (
          <span className="text-muted-foreground italic">none</span>
        ) : (
          <div className="space-y-1">
            {activeReservations.map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-100 w-fit"
              >
                <Boxes className="w-3 h-3" />
                <span className="font-medium">{r.source_shop_name}</span>
                <span className="font-mono">×{r.quantity}</span>
                <button
                  onClick={() => onReleaseReservation(r.id)}
                  title="Release reservation"
                  className="ml-1 text-blue-600 hover:text-red-600"
                  data-testid={`mgmt-row-release-${r.id}`}
                >
                  <Unlock className="w-3 h-3" />
                </button>
              </div>
            ))}
            {remaining > 0 && (
              <div className="text-[10px] text-amber-700">
                +{remaining} still to reserve
              </div>
            )}
          </div>
        )}
      </td>
      <td className="p-2 text-xs text-muted-foreground">{item.requested_by_name || "—"}</td>
      <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">{created}</td>
      <td className="p-2 text-right">
        <div className="flex items-center justify-end gap-1">
          {canReserve && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-blue-700 hover:bg-blue-50"
              onClick={onReserve}
              title="Reserve from a source shop"
              data-testid={`mgmt-row-reserve-${item.id}`}
            >
              <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
              Reserve
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
            onClick={onDelete}
            data-testid={`mgmt-row-delete-${item.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function ManualRequestDialog({ open, onOpenChange, shops, onSubmit }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [product, setProduct] = useState(null);
  const [destShop, setDestShop] = useState("");
  const [qty, setQty] = useState("");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");

  // reset whenever the dialog open state changes (open OR close)
  useEffect(() => {
    setSearchQuery("");
    setResults([]);
    setProduct(null);
    setDestShop("");
    setQty("");
    setPriority("normal");
    setNotes("");
  }, [open]);

  // debounced product search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/products", {
          params: { q: searchQuery, limit: 8 },
        });
        setResults(data.items || []);
      } catch (e) {
        // Debounced lookup — don't toast on every keystroke failure, but
        // keep a console trace so devs can still diagnose silent failures.
        console.warn("ManagementTab: product search failed", e);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const canSubmit = product && destShop && Number(qty) > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      product_id: product.id,
      destination_shop_id: destShop,
      quantity: Number(qty),
      priority,
      notes: notes.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mgmt-add-dialog">
        <DialogHeader>
          <DialogTitle>Add Manual Stock Request</DialogTitle>
          <DialogDescription>
            Request stock for a specific shop. Duplicate-aware — you'll be prompted if a similar
            request already exists.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Product search */}
          <div>
            <Label className="text-xs">Product</Label>
            {!product ? (
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-8 h-9"
                  placeholder="Search (min 2 chars) by name, barcode, brand..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="mgmt-add-product-search"
                  autoFocus
                />
                {searching && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">
                    Searching…
                  </span>
                )}
                {results.length > 0 && (
                  <Card className="absolute z-50 w-full mt-1 max-h-56 overflow-y-auto shadow-lg">
                    {results.map((p) => (
                      <button
                        key={p.id}
                        className="w-full p-2 text-left hover:bg-muted border-b last:border-b-0"
                        onClick={() => {
                          setProduct(p);
                          setResults([]);
                        }}
                        data-testid={`mgmt-add-result-${p.id}`}
                      >
                        <div className="text-sm font-medium">{p.product}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {p.brand_name} · {p.auto_barcode}
                        </div>
                      </button>
                    ))}
                  </Card>
                )}
              </div>
            ) : (
              <div className="mt-1 flex items-center justify-between gap-2 px-3 py-2 border rounded-md">
                <div>
                  <div className="text-sm font-medium">{product.product}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {product.brand_name} · {product.auto_barcode}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setProduct(null)}
                  data-testid="mgmt-add-clear-product"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Shop + qty row */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Destination Shop</Label>
              <Select value={destShop} onValueChange={setDestShop}>
                <SelectTrigger className="h-9 mt-1" data-testid="mgmt-add-shop">
                  <SelectValue placeholder="Select shop" />
                </SelectTrigger>
                <SelectContent>
                  {shops.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Quantity</Label>
              <Input
                type="number"
                min="1"
                className="h-9 mt-1"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="Units needed"
                data-testid="mgmt-add-qty"
              />
            </div>
          </div>

          {/* Priority */}
          <div>
            <Label className="text-xs">Priority</Label>
            <div className="mt-1 flex gap-1.5">
              {PRIORITIES.map((p) => (
                <button
                  key={p}
                  onClick={() => setPriority(p)}
                  className={`px-3 py-1 rounded text-xs border transition ${
                    priority === p
                      ? PRIORITY_STYLE[p].color + " ring-1 ring-offset-1 ring-[#9E6D5B]"
                      : "bg-white border-gray-200 hover:border-[#9E6D5B]"
                  }`}
                  data-testid={`mgmt-add-priority-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input
              className="h-9 mt-1"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why is this needed?"
              data-testid="mgmt-add-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            className="text-white"
            style={{ backgroundColor: "#9E6D5B" }}
            onClick={handleSubmit}
            data-testid="mgmt-add-submit"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add to Queue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DuplicateDialog({ state, onCancel, onMerge, onSeparate }) {
  if (!state) return null;
  const { existing, payload } = state;
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="mgmt-duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Duplicate request detected
          </DialogTitle>
          <DialogDescription>
            A pending restock request already exists for this product at this shop.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <Card className="p-3 bg-amber-50/40 border-amber-200">
            <div className="font-medium">{existing.product_snapshot?.product_name}</div>
            <div className="text-xs text-muted-foreground">
              {existing.destination_shop_name} ·{" "}
              <span className="font-mono">{existing.quantity_requested} units</span> · priority{" "}
              <span className="uppercase">{existing.priority}</span> · status {existing.status}
            </div>
            {existing.notes && (
              <div className="mt-2 text-xs italic text-muted-foreground">"{existing.notes}"</div>
            )}
          </Card>
          <Card className="p-3 bg-slate-50 border-slate-200">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Your new request
            </div>
            <div className="text-sm">
              <span className="font-mono font-semibold">{payload.quantity} units</span> ·{" "}
              priority <span className="uppercase">{payload.priority}</span>
            </div>
          </Card>
        </div>
        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onCancel} data-testid="mgmt-dup-cancel">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={onSeparate}
            data-testid="mgmt-dup-separate"
            className="border-slate-300"
          >
            <CopyIcon className="w-4 h-4 mr-1" />
            Create Separate
          </Button>
          <Button
            onClick={onMerge}
            className="text-white"
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="mgmt-dup-merge"
          >
            <GitMerge className="w-4 h-4 mr-1" />
            Merge ({existing.quantity_requested} + {payload.quantity} ={" "}
            {Number(existing.quantity_requested) + Number(payload.quantity)})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Phase 2 — Reserve Stock Dialog
function ReserveStockDialog({ item, shops, onCancel, onConfirm }) {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sourceShop, setSourceShop] = useState("");
  const [qty, setQty] = useState("");

  // Reset & fetch availability when item opens
  useEffect(() => {
    if (!item) {
      setAvailability(null);
      setSourceShop("");
      setQty("");
      return;
    }
    setLoading(true);
    api
      .get(`/management/availability/${item.product_id}`)
      .then(({ data }) => setAvailability(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [item]);

  if (!item) return null;

  const activeReservations = (item.reservations || []).filter((r) => !r.released_at);
  const alreadyReserved = activeReservations.reduce(
    (s, r) => s + Number(r.quantity || 0),
    0
  );
  const remaining = Math.max(
    0,
    Number(item.quantity_requested) -
      Number(item.quantity_fulfilled || 0) -
      alreadyReserved
  );

  // Eligible source shops = on_hand > 0, not destination, available > 0
  const sourceShops = (availability?.shops || [])
    .filter(
      (s) =>
        s.shop_id !== item.destination_shop_id &&
        s.on_hand > 0 &&
        s.available > 0
    )
    .sort((a, b) => b.available - a.available);

  const selectedShop = sourceShops.find((s) => s.shop_id === sourceShop);
  const maxQty = selectedShop ? Math.min(selectedShop.available, remaining) : 0;
  const qtyNum = Number(qty || 0);
  const canSubmit =
    !!sourceShop && qtyNum > 0 && qtyNum <= maxQty && remaining > 0;

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="mgmt-reserve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="w-5 h-5 text-blue-600" />
            Reserve Stock
          </DialogTitle>
          <DialogDescription>
            Hold stock aside at a source shop so it isn&apos;t billed to a sale before
            the transfer.
          </DialogDescription>
        </DialogHeader>

        <Card className="p-3 bg-slate-50 border-slate-200">
          <div className="text-sm font-medium">
            {item.product_snapshot?.product_name}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            For: <span className="font-medium">{item.destination_shop_name}</span> ·{" "}
            Requested <span className="font-mono">{item.quantity_requested}</span> ·{" "}
            Already reserved <span className="font-mono">{alreadyReserved}</span>
          </div>
          <div className="text-xs text-blue-700 font-medium mt-1">
            Remaining to reserve: <span className="font-mono">{remaining}</span>
          </div>
        </Card>

        <div data-testid="mgmt-reserve-availability">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
            Pick a source shop
          </div>
          {loading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading availability…
            </div>
          ) : sourceShops.length === 0 ? (
            <div className="p-4 text-center text-sm text-amber-700 bg-amber-50 rounded border border-amber-200">
              <AlertTriangle className="w-5 h-5 mx-auto mb-1" />
              No other shop has available stock for this product.
            </div>
          ) : (
            <div className="border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs">
                  <tr>
                    <th className="p-2 text-left">Source Shop</th>
                    <th className="p-2 text-right">On Hand</th>
                    <th className="p-2 text-right">Reserved</th>
                    <th className="p-2 text-right">Available</th>
                    <th className="p-2 text-center w-[40px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {sourceShops.map((s) => (
                    <tr
                      key={s.shop_id}
                      className={`border-t cursor-pointer transition-colors ${
                        sourceShop === s.shop_id
                          ? "bg-blue-50"
                          : "hover:bg-muted/30"
                      }`}
                      onClick={() => {
                        setSourceShop(s.shop_id);
                        const newMax = Math.min(s.available, remaining);
                        setQty(String(newMax));
                      }}
                      data-testid={`mgmt-reserve-source-row-${s.shop_id}`}
                    >
                      <td className="p-2 font-medium">{s.shop_name}</td>
                      <td className="p-2 text-right font-mono">{s.on_hand}</td>
                      <td className="p-2 text-right font-mono text-amber-700">
                        {s.reserved}
                      </td>
                      <td className="p-2 text-right font-mono font-semibold text-blue-700">
                        {s.available}
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="radio"
                          name="source-shop"
                          checked={sourceShop === s.shop_id}
                          onChange={() => setSourceShop(s.shop_id)}
                          aria-label={`Select ${s.shop_name}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedShop && (
          <div>
            <Label className="text-xs">Quantity to reserve (max {maxQty})</Label>
            <Input
              type="number"
              min="1"
              max={maxQty}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="h-9 mt-1"
              data-testid="mgmt-reserve-qty"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="mgmt-reserve-cancel">
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            className="text-white"
            style={{ backgroundColor: "#9E6D5B" }}
            onClick={() => onConfirm(sourceShop, qtyNum)}
            data-testid="mgmt-reserve-confirm"
          >
            <Boxes className="w-4 h-4 mr-1" />
            Reserve {qtyNum > 0 ? qtyNum : ""} units
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
